import axios from 'axios';
import {
  DiagnosticsService,
  type DiagnosticParamPost,
  type DiagnosticType,
} from './diagnosticsService';

export type { DiagnosticType };

/** Envelope of every `DiagnosticResult` SSE event. */
export interface DiagnosticResultEvent<T> {
  id: number;
  type: 'PING' | 'TRACEROUTE' | 'DOWNLOAD' | 'UPLOAD' | 'WIFI';
  result: T;
  createdAt: string;
}

// ─── Result shapes ────────────────────────────────────────────────────────────
// Values arrive flat (plain strings/numbers), not as DiagnosticParam objects.

export interface PingSseResult {
  status: string | null;
  host: string | null;
  dscp: string | null;
  interfaceTest: string | null;
  numberRepetitions: string | null;
  timeout: string | null;
  successCount: string | null;
  failureCount: string | null;
  averageTime: string | null;
  minimumTime: string | null;
  maximumTime: string | null;
}

export interface TracerouteSseHop {
  host: string | null;
  hostAddress: string | null;
  errorCode: string | null;
  /** Comma-separated round-trip times, e.g. "2,2,2" */
  latency: string | null;
}

export interface TracerouteSseResult {
  status: string | null;
  host: string | null;
  dscp: string | null;
  interfaceTest: string | null;
  timeout: string | null;
  responseTime: string | null;
  result: TracerouteSseHop[] | null;
}

export interface SpeedSseResult {
  status: string | null;
  url: string | null;
  dscp: string | null;
  interfaceTest: string | null;
  speedMbps: number;
}

export interface WifiSseNetwork {
  ssid: string | null;
  channel: string | null;
  operatingStandards: string | null;
  rssi: string | null;
  frequency: string | null;
  bandWidth: string | null;
  macAddress: string | null;
  noise: string | null;
  authentication: string | null;
  encryption: string | null;
}

/** The scan reports one status per radio. */
export interface WifiSseResult {
  status1: string | null;
  status2: string | null;
  result: WifiSseNetwork[] | null;
}

// ─── Runner ───────────────────────────────────────────────────────────────────

/** Matches the server-side diagnostic timeout. */
export const DIAGNOSTIC_SSE_TIMEOUT_MS = 320_000;

/** SSE event name carrying the finished diagnostic. */
const RESULT_EVENT = 'DiagnosticResult';

/** The only status the trigger POST is expected to answer with. */
const ACCEPTED_STATUS = 202;

export class DiagnosticTimeoutError extends Error {
  constructor(message = 'Diagnostic timed out') {
    super(message);
    this.name = 'DiagnosticTimeoutError';
  }
}

export class DiagnosticAbortError extends Error {
  constructor(message = 'Diagnostic aborted') {
    super(message);
    this.name = 'DiagnosticAbortError';
  }
}

/**
 * Triggered when the POST answered anything other than 202 Accepted,
 * or when the POST failed to reach the device, getting no status at all
 */
export class DiagnosticTriggerError extends Error {
  readonly status: number | null;

  constructor(status: number | null) {
    super(
      status === null
        ? 'Diagnostic trigger request failed'
        : `Diagnostic trigger answered ${status}, expected ${ACCEPTED_STATUS}`
    );
    this.name = 'DiagnosticTriggerError';
    this.status = status;
  }
}

/**
 * Builds the notify stream URL. Kept in one place so the prefix can be
 * adjusted without touching call sites.
 */
export function buildDiagnosticSseUrl(
  deviceId: string,
  type: DiagnosticType
): string {
  return `/api/notify/${deviceId}/diagnostics/${type}`;
}

/** The backend reports 'Complete' for ping/traceroute and 'Completed' for speed. */
export function isCompleteStatus(status: string | null | undefined): boolean {
  return status === 'Complete' || status === 'Completed';
}

/** Known device-reported failure states, anything else falls back to Error_Other. */
const DIAGNOSTIC_STATES = [
  'DeviceOffline',
  'Unsupported',
  'Timeout',
  'Error_Internal',
] as const;

/**
 * Collapses the per-radio wifi statuses into a single DiagnosticsState:
 * complete only when every reported status is complete.
 */
export function toDiagnosticsState(
  ...statuses: (string | null | undefined)[]
): DiagnosticsState {
  const reported = statuses.filter((s): s is string => Boolean(s));
  if (reported.length === 0) return 'None';
  if (reported.every(isCompleteStatus)) return 'Complete';

  const failure = reported.find((s) => !isCompleteStatus(s));
  const known = DIAGNOSTIC_STATES.find((state) => state === failure);
  return known ?? 'Error_Other';
}

interface RunDiagnosticOptions {
  deviceId: string;
  type: DiagnosticType;
  body: DiagnosticParamPost[];
  signal?: AbortSignal;
  timeoutMs?: number;
}

/**
 * Runs a diagnostic under the SSE architecture:
 * * Open the notify stream
 * * POST the trigger to start the diagnostic
 * * Wait for the result event to arrive on the stream
 * * Resolve with the result, or reject on timeout/abort/error
 */
export function runDiagnostic<T>({
  deviceId,
  type,
  body,
  signal,
  timeoutMs = DIAGNOSTIC_SSE_TIMEOUT_MS,
}: RunDiagnosticOptions): Promise<DiagnosticResultEvent<T>> {
  return new Promise<DiagnosticResultEvent<T>>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DiagnosticAbortError());
      return;
    }

    const eventSource = new EventSource(buildDiagnosticSseUrl(deviceId, type));

    let settled = false;
    let received: DiagnosticResultEvent<T> | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // Closes the stream and detaches every listener. Safe to call twice.
    const teardown = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      window.removeEventListener('pagehide', onPageHide);
      signal?.removeEventListener('abort', onAbort);
      eventSource.close();
    };

    const settleResolve = (value: DiagnosticResultEvent<T>) => {
      if (settled) return;
      settled = true;
      teardown();
      resolve(value);
    };

    const settleReject = (error: Error) => {
      if (settled) return;
      settled = true;
      teardown();
      reject(error);
    };

    function onPageHide() {
      settleReject(new DiagnosticAbortError('Page hidden'));
    }

    function onAbort() {
      settleReject(new DiagnosticAbortError());
    }

    window.addEventListener('pagehide', onPageHide);
    signal?.addEventListener('abort', onAbort);

    timer = setTimeout(
      () => settleReject(new DiagnosticTimeoutError()),
      timeoutMs
    );

    eventSource.addEventListener(RESULT_EVENT, (event) => {
      try {
        received = JSON.parse((event as MessageEvent).data);
      } catch (parseError) {
        settleReject(
          new Error(
            `Failed to parse ${RESULT_EVENT} payload: ${String(parseError)}`
          )
        );
        return;
      }
      settleResolve(received as DiagnosticResultEvent<T>);
    });

    // Triggered on a real connection failure and when the server closes the stream.
    eventSource.onerror = () => {
      if (received) {
        settleResolve(received);
      } else {
        settleReject(new Error(`SSE connection failed for ${type}`));
      }
    };

    // Fired without waiting for `onopen`, which may never come before the POST.
    // Only 202 means the diagnostic started; anything else closes the stream.
    DiagnosticsService.trigger(deviceId, type, body)
      .then((status) => {
        if (status !== ACCEPTED_STATUS) {
          settleReject(new DiagnosticTriggerError(status));
        }
      })
      .catch((postError: unknown) => {
        // Where 4xx/5xx land, since axios rejects every non-2xx response.
        settleReject(
          new DiagnosticTriggerError(
            axios.isAxiosError(postError)
              ? (postError.response?.status ?? null)
              : null
          )
        );
      });
  });
}
